import { defaultHeaders } from 'app/api';
import { Config } from 'app/apiTypes';
import { getAuth, getIdTokenResult } from 'firebase/auth';
import { getEnv } from 'lib/env';

import * as Sentry from '@sentry/react';

import { UserClaims } from './types';

const DSN = process.env.REACT_APP_SENTRY_DSN!;

export async function initSentry() {
  if (process.env.NODE_ENV === 'development') return;

  const client = Sentry.init({
    dsn: DSN,
    environment: await getEnv(),
    sendDefaultPii: true,
    integrations: [
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [400, 599] }),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [window.location.origin, 'april', 'limep', 'spenda'],
        networkRequestHeaders: [],
        networkResponseHeaders: ['request-id'],
        networkCaptureBodies: true,
      }),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  const replayUrl = getReplayUrl();
  if (replayUrl) {
    defaultHeaders['Logrocket-Session-Url'] = replayUrl;
  }

  return client;
}

export const getReplayUrl = () => {
  const replayId = Sentry.getReplay()?.getReplayId();
  if (!replayId) return;

  return `https://spenda.sentry.io/explore/replays/${replayId}`;
};

export async function captureConfig(config: Config) {
  const tags = {
    'merchant.id': config.merchantId,
    'marketplace.id': config.marketplaceId,
    session_correlation_id: defaultHeaders['Session-Correlation-Id'],
  };
  Sentry.setTags(tags);
}

export async function captureUser() {
  const auth = getAuth();
  if (!auth.currentUser) return;

  const idTokenResult = await getIdTokenResult(auth.currentUser);
  if (!idTokenResult) return;

  const claims = idTokenResult.claims as unknown as UserClaims;

  const tags = {
    'customer.id': claims.limepay?.customerId,
    'customer.delegate_id': claims.customerDelegate?.userId,
  };

  const user = {
    id: claims.customerDelegate?.userId ?? claims.limepay?.customerId,
    email: claims.customerDelegate?.emailAddress ?? claims.email,
  };

  Sentry.setTags(tags);
  Sentry.setUser(user);
}

export const captureException = Sentry.captureException;

export const captureMessage = Sentry.captureMessage;
